import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCategories } from "@/context/CategoriesContext";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function startOfMonth(d) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function Legend() {
  const { sports } = useCategories();
  return (
    <div className="flex flex-wrap gap-x-5 gap-y-2 mb-4">
      {sports.map((s) => (
        <span key={s.id} className="inline-flex items-center gap-2 overline text-zinc-400">
          <span className="w-3 h-3 inline-block" style={{ backgroundColor: s.color }} />
          {s.label}
        </span>
      ))}
    </div>
  );
}

function EventChip({ e, onEventClick }) {
  const { sportColor, sportLabel } = useCategories();
  const c = sportColor(e.sport);
  const t = new Date(e.start_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  return (
    <button
      data-testid={`cal-event-${e.id}`}
      onClick={() => onEventClick(e.id)}
      title={`${sportLabel(e.sport)} · ${e.title}`}
      className="w-full text-left px-1.5 py-1 truncate hover:brightness-125 transition-all"
      style={{ backgroundColor: `${c}22`, borderLeft: `3px solid ${c}` }}
    >
      <span className="text-[11px] text-zinc-200 leading-tight block truncate">
        <span style={{ color: c }} className="font-semibold">{t}</span> {e.title}
      </span>
    </button>
  );
}

export default function CalendarMonth({ events, onEventClick, days }) {
  const isRange = !!days; // day-range mode (7/14/31) vs full month
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));

  const byDay = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      const d = new Date(e.start_at);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      (map[key] = map[key] || []).push(e);
    });
    return map;
  }, [events]);

  const today = new Date();
  const isToday = (d) => d && d.toDateString() === today.toDateString();
  const keyOf = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

  // ----- Day-range mode (rolling N days from today) -----
  const rangeCells = useMemo(() => {
    if (!isRange) return [];
    const start = new Date(); start.setHours(0, 0, 0, 0);
    return Array.from({ length: days }, (_, i) => {
      const d = new Date(start); d.setDate(start.getDate() + i); return d;
    });
  }, [isRange, days]);

  // Month grid cells (hook must run unconditionally, before any early return).
  const cells = useMemo(() => {
    const firstDay = startOfMonth(cursor);
    const lead = (firstDay.getDay() + 6) % 7; // Monday-first
    const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const arr = [];
    for (let i = 0; i < lead; i++) arr.push(null);
    for (let day = 1; day <= daysInMonth; day++) arr.push(new Date(cursor.getFullYear(), cursor.getMonth(), day));
    while (arr.length % 7 !== 0) arr.push(null);
    return arr;
  }, [cursor]);

  if (isRange) {
    const first = rangeCells[0];
    const last = rangeCells[rangeCells.length - 1];
    const fmtShort = (d) => d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
    const tall = days <= 7;
    return (
      <div data-testid="calendar-range">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading text-4xl tracking-wide leading-none">
            Next {days} Days{" "}
            <span className="text-ec-volt text-2xl align-middle">{fmtShort(first)} – {fmtShort(last)}</span>
          </h3>
        </div>
        <Legend />
        <div className={`grid gap-px bg-white/10 border border-white/10 ${tall ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" : "grid-cols-2 sm:grid-cols-4 lg:grid-cols-7"}`}>
          {rangeCells.map((d) => {
            const dayEvents = byDay[keyOf(d)] || [];
            return (
              <div key={keyOf(d)} className={`bg-ec-bg p-2 ${tall ? "min-h-[150px]" : "min-h-[120px]"}`}>
                <div className={`flex items-baseline gap-1.5 mb-2 px-0.5 ${isToday(d) ? "text-ec-volt" : "text-zinc-400"}`}>
                  <span className="overline">{d.toLocaleDateString("en-GB", { weekday: "short" })}</span>
                  <span className="font-heading text-xl leading-none">{d.getDate()}</span>
                  {isToday(d) && <span className="overline text-black bg-ec-volt px-1 leading-none">Today</span>}
                </div>
                <div className="space-y-1">
                  {dayEvents.length === 0 && <span className="text-[11px] text-zinc-600 px-1">—</span>}
                  {dayEvents.map((e) => <EventChip key={e.id} e={e} onEventClick={onEventClick} />)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ----- Full month mode -----
  const move = (delta) => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1));

  return (
    <div data-testid="calendar-month">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-heading text-4xl tracking-wide leading-none">
          {cursor.toLocaleDateString("en-GB", { month: "long" })}{" "}
          <span className="text-ec-volt">{cursor.getFullYear()}</span>
        </h3>
        <div className="flex items-center gap-2">
          <button data-testid="cal-today" onClick={() => setCursor(startOfMonth(new Date()))} className="overline border border-white/15 px-3 py-2 hover:border-ec-volt hover:text-ec-volt transition-colors">Today</button>
          <button data-testid="cal-prev" onClick={() => move(-1)} className="border border-white/15 p-2.5 hover:border-ec-volt hover:text-ec-volt transition-colors"><ChevronLeft size={16} /></button>
          <button data-testid="cal-next" onClick={() => move(1)} className="border border-white/15 p-2.5 hover:border-ec-volt hover:text-ec-volt transition-colors"><ChevronRight size={16} /></button>
        </div>
      </div>

      <Legend />

      <div className="grid grid-cols-7 border-t border-l border-white/10">
        {WEEKDAYS.map((w) => (
          <div key={w} className="overline text-zinc-500 p-2.5 border-r border-b border-white/10 bg-black/40 text-center">{w}</div>
        ))}
        {cells.map((d, i) => {
          const key = d ? keyOf(d) : `b-${i}`;
          const dayEvents = d ? byDay[key] || [] : [];
          return (
            <div key={key} className={`min-h-[112px] border-r border-b border-white/10 p-1.5 align-top ${d ? "" : "bg-white/[0.015]"}`}>
              {d && (
                <>
                  <div className={`text-sm font-semibold mb-1 px-1 ${isToday(d) ? "text-black bg-ec-volt inline-block leading-none py-0.5" : "text-zinc-400"}`}>{d.getDate()}</div>
                  <div className="space-y-1">
                    {dayEvents.slice(0, 3).map((e) => <EventChip key={e.id} e={e} onEventClick={onEventClick} />)}
                    {dayEvents.length > 3 && <span className="block text-[10px] text-zinc-500 px-1.5">+{dayEvents.length - 3} more</span>}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
